import { useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, updateDoc, deleteDoc, addDoc, doc, serverTimestamp, query, where } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../../firebase';
import { useExpenseCategories } from '../../utils/expenseCategories';

function guessCategory(serviceType) {
  const s = (serviceType || '').toLowerCase();
  if (/hotel|accommodat/.test(s))      return 'Accommodation';
  if (/flight|air/.test(s))            return 'Flights';
  if (/transport|bus|transfer|coach/.test(s)) return 'Ground Transport';
  if (/match|ticket/.test(s))          return 'Match Tickets';
  if (/stadium|venue/.test(s))         return 'Stadium / Venue';
  if (/train/.test(s))                 return 'Training Facilities';
  if (/meal|food|cater/.test(s))       return 'Meals & Catering';
  return 'Other';
}

function LinkExpenseCell({ assignment, expenses, linkedExpenseMap, requestMap, expenseCategories }) {
  const linked = linkedExpenseMap[assignment.id];
  const [showCreate, setShowCreate] = useState(false);
  const serviceTypeLabel = Array.isArray(assignment.serviceTypes) && assignment.serviceTypes.length
    ? assignment.serviceTypes.join(', ')
    : (assignment.serviceType || '');
  const [category,   setCategory]   = useState(() => guessCategory(serviceTypeLabel));
  const [description, setDescription] = useState(`${assignment.supplierName || ''}${serviceTypeLabel ? ` — ${serviceTypeLabel}` : ''}`);
  const [date,       setDate]        = useState(new Date().toISOString().slice(0, 10));
  const [amount,     setAmount]      = useState(assignment.invoiceAmount != null ? String(assignment.invoiceAmount) : '');
  const [currency,   setCurrency]    = useState(assignment.invoiceCurrency || 'GBP');
  const [saving,     setSaving]      = useState(false);

  const linkable = expenses.filter(
    (e) => e.requestId === assignment.requestId &&
      (!e.linkedSupplierAssignmentId || e.linkedSupplierAssignmentId === assignment.id)
  );

  async function handleCreate(e) {
    e.preventDefault();
    if (!amount || !date) return;
    setSaving(true);
    try {
      const req = requestMap?.[assignment.requestId];
      await addDoc(collection(db, 'expenses'), {
        category, description: description.trim(),
        amount: Number(amount), currency,
        gbpEquivalent: currency === 'GBP' ? Number(amount) : Number(amount),
        date, requestId: assignment.requestId,
        requestSummary: req ? (req.internalRef || req.tourName || assignment.requestId) : assignment.requestId,
        brand: 'mst',
        createdAt: serverTimestamp(),
        linkedSupplierAssignmentId: assignment.id,
      });
      // pull amount and due date onto the assignment if not already set
      const supplierUpdate = {};
      if (assignment.invoiceAmount == null && amount) {
        supplierUpdate.invoiceAmount = Number(amount);
        supplierUpdate.invoiceCurrency = currency;
      }
      if (!assignment.invoiceDueDate && !assignment.invoicePaymentDate && date) {
        supplierUpdate.invoiceDueDate = date;
      }
      if (Object.keys(supplierUpdate).length > 0) {
        await updateDoc(doc(db, 'bookingSuppliers', assignment.id), supplierUpdate);
      }
      setShowCreate(false);
    } catch (err) {
      console.error(err);
      alert('Failed to create expense.');
    } finally {
      setSaving(false);
    }
  }

  if (linked) {
    return (
      <span className="inline-flex items-center gap-1 mt-1 text-[10.5px] font-semibold text-[#1A4A8A] bg-[#EFF6FF] border border-[#93C5FD] px-2 py-0.5 rounded-full">
        📎 {linked.description || linked.category || 'Expense'}
        <button
          onClick={() => updateDoc(doc(db, 'expenses', linked.id), { linkedSupplierAssignmentId: null })}
          className="text-[#93C5FD] hover:text-dr leading-none ml-0.5"
          title="Remove link"
        >×</button>
      </span>
    );
  }

  if (showCreate) {
    return (
      <form onSubmit={handleCreate} className="mt-1 space-y-1.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-[8px] p-2 w-[220px]">
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          className="w-full text-[11px] border border-[#E5E7EB] rounded-[6px] px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-pb">
          {expenseCategories.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Description" className="w-full text-[11px] border border-[#E5E7EB] rounded-[6px] px-2 py-1 focus:outline-none focus:ring-1 focus:ring-pb" />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="w-full text-[11px] border border-[#E5E7EB] rounded-[6px] px-2 py-1 focus:outline-none focus:ring-1 focus:ring-pb" />
        <div className="flex gap-1">
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}
            className="text-[11px] border border-[#E5E7EB] rounded-[6px] px-1.5 py-1 bg-white focus:outline-none w-[52px]">
            <option>GBP</option><option>EUR</option><option>USD</option>
          </select>
          <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder="Amount" required
            className="flex-1 text-[11px] border border-[#E5E7EB] rounded-[6px] px-2 py-1 focus:outline-none focus:ring-1 focus:ring-pb" />
        </div>
        <div className="flex gap-1.5">
          <button type="submit" disabled={saving || !amount || !date}
            className="flex-1 text-[11px] font-bold text-white bg-pb hover:bg-navy rounded-[6px] py-1 transition disabled:opacity-50">
            {saving ? 'Saving…' : 'Create'}
          </button>
          <button type="button" onClick={() => setShowCreate(false)}
            className="text-[11px] text-[#9CA3AF] hover:text-[#374151] px-1">✕</button>
        </div>
      </form>
    );
  }

  return (
    <div className="mt-1 flex flex-col gap-0.5">
      {linkable.length > 0 && (
        <select
          defaultValue=""
          onChange={(e) => {
            if (e.target.value) {
              const selectedExpense = expenses.find((ex) => ex.id === e.target.value);
              updateDoc(doc(db, 'expenses', e.target.value), { linkedSupplierAssignmentId: assignment.id });
              if (selectedExpense) {
                const supplierUpdate = {};
                if (assignment.invoiceAmount == null && selectedExpense.amount != null) {
                  supplierUpdate.invoiceAmount = Number(selectedExpense.amount);
                  supplierUpdate.invoiceCurrency = selectedExpense.currency || 'GBP';
                }
                if (!assignment.invoiceDueDate && !assignment.invoicePaymentDate && selectedExpense.date) {
                  supplierUpdate.invoiceDueDate = selectedExpense.date;
                }
                if (Object.keys(supplierUpdate).length > 0) {
                  updateDoc(doc(db, 'bookingSuppliers', assignment.id), supplierUpdate);
                }
              }
              e.target.value = '';
            }
          }}
          className="text-[10.5px] text-[#9CA3AF] border-0 bg-transparent focus:outline-none cursor-pointer hover:text-pb max-w-[180px] truncate"
        >
          <option value="">+ Link existing expense…</option>
          {linkable.map((e) => (
            <option key={e.id} value={e.id}>
              {e.description || e.category || 'Expense'}{e.amount ? ` (${e.currency || 'GBP'} ${Number(e.amount).toFixed(2)})` : ''}
            </option>
          ))}
        </select>
      )}
      <button onClick={() => setShowCreate(true)}
        className="text-[10.5px] font-semibold text-pb hover:text-navy transition text-left">
        + Create &amp; link expense
      </button>
    </div>
  );
}

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function formatTs(ts) {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

function fmtAmount(amount, currency) {
  if (amount == null || amount === '') return null;
  const sym = CURRENCY_SYMBOLS[currency] || '£';
  return `${sym}${Number(amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtMulti(byCurrency) {
  const entries = Object.entries(byCurrency).filter(([, v]) => v > 0);
  if (entries.length === 0) return '—';
  return entries
    .map(([c, v]) => `${CURRENCY_SYMBOLS[c] || c}${v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
    .join(' + ');
}

const STATUS_STYLES = {
  pending:   'bg-amber-light text-[#92400E]',
  confirmed: 'bg-green-light text-[#065F46]',
  declined:  'bg-red-light text-dr',
};
const STATUS_LABELS = { pending: 'Pending', confirmed: 'Confirmed', declined: 'Declined' };

function AmountCell({ assignment }) {
  const [editing,  setEditing]  = useState(false);
  const [amount,   setAmount]   = useState(assignment.invoiceAmount != null ? String(assignment.invoiceAmount) : '');
  const [currency, setCurrency] = useState(assignment.invoiceCurrency || 'GBP');
  const [saving,   setSaving]   = useState(false);

  async function save() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'bookingSuppliers', assignment.id), {
        invoiceAmount:   amount !== '' ? Number(amount) : null,
        invoiceCurrency: currency,
      });
      setEditing(false);
    } catch (err) {
      console.error(err);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-2 py-1">
        <div className="flex gap-1.5 items-center">
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="border border-[#E5E7EB] rounded-[7px] px-2 py-[5px] text-[12px] focus:outline-none focus:ring-1 focus:ring-pb bg-white w-[58px]"
          >
            <option>GBP</option>
            <option>EUR</option>
            <option>USD</option>
          </select>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="border border-[#E5E7EB] rounded-[7px] px-2 py-[5px] text-[12px] focus:outline-none focus:ring-1 focus:ring-pb w-[88px]"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={saving}
            className="px-3 py-[5px] text-[11.5px] font-bold text-white bg-pb hover:bg-navy rounded-[7px] transition disabled:opacity-60">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => { setEditing(false); setAmount(assignment.invoiceAmount != null ? String(assignment.invoiceAmount) : ''); setCurrency(assignment.invoiceCurrency || 'GBP'); }}
            className="px-3 py-[5px] text-[11.5px] text-[#6B7280] hover:text-[#374151] transition">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {assignment.invoiceAmount != null ? (
        <p className="text-[13px] font-bold text-[#111827] tabular-nums">
          {fmtAmount(assignment.invoiceAmount, assignment.invoiceCurrency || 'GBP')}
        </p>
      ) : (
        <p className="text-[12px] text-[#D1D5DB]">—</p>
      )}
      <button onClick={() => setEditing(true)}
        className="text-[11px] font-semibold text-pb hover:text-navy transition text-left">
        {assignment.invoiceAmount != null ? 'Edit' : '+ Add amount'}
      </button>
    </div>
  );
}

function DueDateCell({ assignment }) {
  const [editing, setEditing] = useState(false);
  // read from new field, fall back to old field for backward compat
  const existing = assignment.invoiceDueDate || assignment.invoicePaymentDate || '';
  const [dueDate, setDueDate] = useState(existing);
  const [saving,  setSaving]  = useState(false);

  const today = new Date(); today.setHours(0,0,0,0);
  const due   = existing ? new Date(existing) : null;
  const isOverdue = due && due < today && !assignment.invoicePaid;
  const daysUntil = due ? Math.round((due - today) / 864e5) : null;

  async function save() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'bookingSuppliers', assignment.id), {
        invoiceDueDate: dueDate || null,
      });
      setEditing(false);
    } catch (err) {
      console.error(err);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-2 py-1">
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="border border-[#E5E7EB] rounded-[7px] px-2 py-[5px] text-[12px] focus:outline-none focus:ring-1 focus:ring-pb w-[140px]"
        />
        <div className="flex gap-2">
          <button onClick={save} disabled={saving}
            className="px-3 py-[5px] text-[11.5px] font-bold text-white bg-pb hover:bg-navy rounded-[7px] transition disabled:opacity-60">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => { setEditing(false); setDueDate(existing); }}
            className="px-3 py-[5px] text-[11.5px] text-[#6B7280] hover:text-[#374151] transition">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {existing ? (
        <>
          <p className={`text-[13px] font-semibold tabular-nums ${isOverdue ? 'text-dr' : 'text-[#111827]'}`}>
            {formatDate(existing)}
          </p>
          {!assignment.invoicePaid && daysUntil !== null && (
            <p className={`text-[10.5px] font-semibold ${isOverdue ? 'text-dr' : daysUntil <= 14 ? 'text-[#92400E]' : 'text-[#9CA3AF]'}`}>
              {isOverdue ? `${Math.abs(daysUntil)}d overdue` : daysUntil === 0 ? 'Due today' : `${daysUntil}d left`}
            </p>
          )}
        </>
      ) : (
        <p className="text-[12px] text-[#D1D5DB]">—</p>
      )}
      <button onClick={() => setEditing(true)}
        className="text-[11px] font-semibold text-pb hover:text-navy transition text-left">
        {existing ? 'Edit' : '+ Add due date'}
      </button>
    </div>
  );
}

function PaidStatusCell({ assignment }) {
  const [saving, setSaving] = useState(false);

  async function markPaid() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'bookingSuppliers', assignment.id), {
        invoicePaid:   true,
        invoicePaidAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(err);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function markUnpaid() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'bookingSuppliers', assignment.id), {
        invoicePaid:   false,
        invoicePaidAt: null,
      });
    } catch (err) {
      console.error(err);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (assignment.invoicePaid) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="inline-flex items-center gap-1 text-[11.5px] font-bold text-sg">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
          Paid
        </span>
        {assignment.invoicePaidAt && (
          <p className="text-[10.5px] text-[#9CA3AF]">
            {assignment.invoicePaidAt.toDate
              ? assignment.invoicePaidAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
              : ''}
          </p>
        )}
        <button onClick={markUnpaid} disabled={saving}
          className="text-[10.5px] text-[#9CA3AF] hover:text-dr transition text-left disabled:opacity-50">
          Undo
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={markPaid}
      disabled={saving}
      className="inline-flex items-center gap-1 px-[10px] py-[5px] text-[12px] font-semibold text-sg border border-sg/30 hover:bg-green-light rounded-[8px] transition disabled:opacity-50 whitespace-nowrap"
    >
      {saving ? 'Saving…' : '✓ Mark paid'}
    </button>
  );
}

const EMPTY_INVOICE_FORM = {
  requestId: '', supplierName: '', supplierEmail: '',
  serviceType: '', amount: '', currency: 'GBP', dueDate: '',
};

function AddInvoiceModal({ requestMap, onClose, onSaved }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState(EMPTY_INVOICE_FORM);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const sortedRequests = Object.entries(requestMap).sort(([, a], [, b]) =>
    (a.departureDate || '').localeCompare(b.departureDate || '')
  );

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.requestId) { setError('Please select a tour.'); return; }
    if (!form.supplierName.trim()) { setError('Please enter a supplier name.'); return; }
    if (!form.serviceType.trim()) { setError('Please enter a service type.'); return; }
    if (!file) { setError('Please select an invoice file.'); return; }
    setSaving(true);
    try {
      const req = requestMap[form.requestId] || {};
      const assignment = await addDoc(collection(db, 'bookingSuppliers'), {
        requestId:     form.requestId,
        sport:         req.sport || '',
        destination:   req.destination || '',
        departureDate: req.departureDate || '',
        returnDate:    req.returnDate || '',
        supplierName:  form.supplierName.trim(),
        supplierEmail: form.supplierEmail.trim(),
        serviceType:   form.serviceType.trim(),
        staffNotes:    '',
        status:        'confirmed',
        createdAt:     serverTimestamp(),
        uploadedByStaff: true,
      });
      const path = `invoices/${form.requestId}/${assignment.id}/${file.name}`;
      const snap = await uploadBytes(storageRef(storage, path), file);
      const url  = await getDownloadURL(snap.ref);
      await updateDoc(doc(db, 'bookingSuppliers', assignment.id), {
        invoiceUrl:        url,
        invoiceFileName:   file.name,
        invoiceUploadedAt: serverTimestamp(),
        ...(form.amount     ? { invoiceAmount: Number(form.amount), invoiceCurrency: form.currency } : {}),
        ...(form.dueDate    ? { invoiceDueDate: form.dueDate } : {}),
      });
      onSaved();
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-[16px] shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
          <h2 className="text-[16px] font-extrabold text-navy">Add Supplier Invoice</h2>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151] text-[20px] leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Tour */}
          <div>
            <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">Tour *</label>
            <select
              value={form.requestId}
              onChange={(e) => set('requestId', e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
            >
              <option value="">Select a tour…</option>
              {sortedRequests.map(([id, r]) => (
                <option key={id} value={id}>
                  {r.internalRef ? `${r.internalRef} — ` : ''}{r.tourName || r.schoolName || id}{r.destination ? ` (${r.destination})` : ''}
                </option>
              ))}
            </select>
          </div>
          {/* Supplier */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">Supplier name *</label>
              <input
                value={form.supplierName}
                onChange={(e) => set('supplierName', e.target.value)}
                placeholder="e.g. Hotel Madrid"
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
              />
            </div>
            <div>
              <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">Supplier email</label>
              <input
                type="email"
                value={form.supplierEmail}
                onChange={(e) => set('supplierEmail', e.target.value)}
                placeholder="Optional"
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
              />
            </div>
          </div>
          {/* Service type */}
          <div>
            <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">Service type *</label>
            <input
              value={form.serviceType}
              onChange={(e) => set('serviceType', e.target.value)}
              placeholder="e.g. Accommodation, Transport, Match fees…"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
            />
          </div>
          {/* Invoice file */}
          <div>
            <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">Invoice file *</label>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <button type="button" onClick={() => fileRef.current?.click()}
              className="w-full border border-dashed border-[#D1D5DB] rounded-[8px] px-3 py-3 text-[13px] text-[#6B7280] hover:border-pb hover:text-pb transition text-center">
              {file ? `📎 ${file.name}` : 'Click to select PDF or image…'}
            </button>
          </div>
          {/* Amount + due date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">Amount</label>
              <div className="flex gap-1.5">
                <select value={form.currency} onChange={(e) => set('currency', e.target.value)}
                  className="border border-[#E5E7EB] rounded-[8px] px-2 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white w-[64px]">
                  <option>GBP</option><option>EUR</option><option>USD</option>
                </select>
                <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)}
                  placeholder="0.00"
                  className="flex-1 border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">Due date</label>
              <input type="date" value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb" />
            </div>
          </div>
          {error && <p className="text-[12px] text-dr">{error}</p>}
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving}
              className="px-5 py-[9px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60">
              {saving ? 'Saving…' : 'Save invoice'}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-[9px] text-[13px] text-[#6B7280] hover:text-[#374151] transition">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StaffInvoiceCell({ assignment }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const path = `invoices/${assignment.requestId}/${assignment.id}/${file.name}`;
      const snap = await uploadBytes(storageRef(storage, path), file);
      const url = await getDownloadURL(snap.ref);
      await updateDoc(doc(db, 'bookingSuppliers', assignment.id), {
        invoiceUrl:        url,
        invoiceFileName:   file.name,
        invoiceUploadedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  return (
    <div className="flex flex-col gap-1">
      {assignment.invoiceUrl && (
        <div>
          <a
            href={assignment.invoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[12.5px] font-bold text-pb hover:text-navy transition"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            {assignment.invoiceFileName || 'Download'}
          </a>
          {assignment.invoiceUploadedAt && (
            <p className="text-[11px] text-[#9CA3AF] mt-0.5">{formatTs(assignment.invoiceUploadedAt)}</p>
          )}
        </div>
      )}
      <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={handleFile} />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-pb hover:text-navy transition disabled:opacity-50 text-left"
      >
        {uploading ? 'Uploading…' : assignment.invoiceUrl ? 'Replace' : (
          <span className="inline-flex items-center gap-1 bg-[#F9FAFB] border border-[#E5E7EB] px-[9px] py-[3px] rounded-[12px] text-[#6B7280] hover:border-pb hover:text-pb transition">
            + Upload invoice
          </span>
        )}
      </button>
      {error && <p className="text-[11px] text-dr">{error}</p>}
    </div>
  );
}

export default function SupplierInvoices() {
  const navigate = useNavigate();
  const expenseCategories = useExpenseCategories();
  const [assignments,      setAssignments]      = useState([]);
  const [requestMap,       setRequestMap]       = useState({});
  const [expenses,         setExpenses]         = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [filter,           setFilter]           = useState('all');
  const [search,           setSearch]           = useState('');
  const [deletingId,       setDeletingId]       = useState(null);
  const [linkedExpenseMap, setLinkedExpenseMap] = useState({});
  const [showAddModal,     setShowAddModal]     = useState(false);

  async function handleDelete(id) {
    if (!window.confirm('Delete this supplier assignment? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, 'bookingSuppliers', id));
    } catch (err) {
      console.error(err);
      alert('Failed to delete. Please try again.');
    } finally {
      setDeletingId(null);
    }
  }

  useEffect(() => {
    let assignmentsDone = false, requestsDone = false;
    const check = () => { if (assignmentsDone && requestsDone) setLoading(false); };

    const u1 = onSnapshot(collection(db, 'bookingSuppliers'), (snap) => {
      setAssignments(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || ''))
      );
      assignmentsDone = true; check();
    });

    const u2 = onSnapshot(collection(db, 'requests'), (snap) => {
      const map = {};
      snap.docs.forEach((d) => { map[d.id] = d.data(); });
      setRequestMap(map);
      requestsDone = true; check();
    });

    const u3 = onSnapshot(collection(db, 'expenses'), (snap) => {
      setExpenses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => { u1(); u2(); u3(); };
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'expenses'), where('linkedSupplierAssignmentId', '!=', null)),
      (snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          if (data.linkedSupplierAssignmentId) {
            map[data.linkedSupplierAssignmentId] = { id: d.id, ...data };
          }
        });
        setLinkedExpenseMap(map);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    if (!assignments.length || !Object.keys(linkedExpenseMap).length) return;
    assignments.forEach((a) => {
      const exp = linkedExpenseMap[a.id];
      if (!exp) return;
      const update = {};
      if (a.invoiceAmount == null && exp.amount != null) {
        update.invoiceAmount = Number(exp.amount);
        update.invoiceCurrency = exp.currency || 'GBP';
      }
      if (!a.invoiceDueDate && !a.invoicePaymentDate && exp.date) {
        update.invoiceDueDate = exp.date;
      }
      if (Object.keys(update).length > 0) {
        updateDoc(doc(db, 'bookingSuppliers', a.id), update);
      }
    });
  }, [linkedExpenseMap, assignments]);

  function tourLabel(a) {
    const req = requestMap[a.requestId];
    if (!req) return a.sport ? `${a.sport} — ${a.destination || ''}`.trim() : a.requestId;
    return req.internalRef || req.tourName || `${req.sport || ''}${req.destination ? ` — ${req.destination}` : ''}`.trim() || a.requestId;
  }

  function tourSubLabel(a) {
    const req = requestMap[a.requestId];
    if (!req) return null;
    if (req.internalRef) return req.tourName || `${req.sport || ''} — ${req.destination || ''}`.trim();
    if (req.tourName) return `${req.sport || ''} — ${req.destination || ''}`.trim();
    return null;
  }

  const total    = assignments.length;
  const received = assignments.filter((a) => !!a.invoiceUrl).length;
  const missing  = total - received;
  const pct      = total > 0 ? Math.round((received / total) * 100) : 0;

  const paidCount   = assignments.filter((a) => !!a.invoicePaid).length;
  const unpaidCount = assignments.filter((a) => a.invoiceUrl && !a.invoicePaid).length;

  const totalByCurrency       = {};
  const paidByCurrency        = {};
  const outstandingByCurrency = {};
  for (const a of assignments) {
    if (a.invoiceAmount == null) continue;
    const c   = a.invoiceCurrency || 'GBP';
    const amt = Number(a.invoiceAmount) || 0;
    totalByCurrency[c] = (totalByCurrency[c] || 0) + amt;
    if (a.invoicePaid) paidByCurrency[c]        = (paidByCurrency[c]        || 0) + amt;
    else               outstandingByCurrency[c] = (outstandingByCurrency[c] || 0) + amt;
  }

  const filtered = assignments.filter((a) => {
    if (filter === 'received' && !a.invoiceUrl) return false;
    if (filter === 'missing'  &&  a.invoiceUrl) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const req = requestMap[a.requestId] || {};
      return (
        (a.supplierName          || '').toLowerCase().includes(q) ||
        (a.supplierEmail         || '').toLowerCase().includes(q) ||
        (a.sport                 || '').toLowerCase().includes(q) ||
        (a.destination           || '').toLowerCase().includes(q) ||
        (Array.isArray(a.serviceTypes) ? a.serviceTypes.join(' ') : (a.serviceType || '')).toLowerCase().includes(q) ||
        (req.internalRef         || '').toLowerCase().includes(q) ||
        (req.tourName            || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div className="space-y-5">

      {showAddModal && (
        <AddInvoiceModal
          requestMap={requestMap}
          onClose={() => setShowAddModal(false)}
          onSaved={() => setShowAddModal(false)}
        />
      )}

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[23px] font-extrabold text-navy tracking-[-0.4px]">Supplier Invoices</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">
            Track invoices from suppliers. Upload on their behalf if needed.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex-shrink-0 flex items-center gap-1.5 bg-pb hover:bg-navy text-white text-[13px] font-bold px-[14px] py-[9px] rounded-[9px] transition shadow-sm"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add invoice
        </button>
      </div>

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          {
            label: 'Invoices received',
            value: `${received}/${total}`,
            sub: `${pct}% receipt rate`,
            accent: '#1A4A8A',
          },
          {
            label: 'Awaiting payment',
            value: unpaidCount,
            sub: unpaidCount > 0 ? 'invoices received, not yet paid' : 'None outstanding',
            accent: unpaidCount > 0 ? '#E8A020' : '#1A7A4A',
          },
          {
            label: 'Paid',
            value: paidCount,
            sub: Object.keys(paidByCurrency).length > 0 ? fmtMulti(paidByCurrency) : 'No invoices marked paid',
            accent: '#1A7A4A',
          },
          {
            label: 'Outstanding',
            value: fmtMulti(outstandingByCurrency),
            sub: Object.keys(outstandingByCurrency).length > 0 ? 'unpaid invoice amounts' : 'All paid',
            accent: Object.keys(outstandingByCurrency).length > 0 ? '#C0392B' : '#1A7A4A',
          },
        ].map(({ label, value, sub, accent }) => (
          <div key={label} className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] px-[16px] py-[15px]">
            <p className="text-[11.5px] font-semibold text-[#6B7280] mb-[8px]">{label}</p>
            <p className="text-[22px] font-extrabold tabular-nums leading-tight" style={{ color: accent }}>{value}</p>
            <p className="text-[11.5px] text-[#9CA3AF] mt-1">{sub}</p>
          </div>
        ))}
      </div>

      {/* ── Filters + search ── */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] px-5 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center bg-surface rounded-[9px] p-[3px] gap-[2px]">
          {[
            { key: 'all',      label: 'All' },
            { key: 'received', label: 'Invoice received' },
            { key: 'missing',  label: 'Not uploaded' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-[6px] rounded-[7px] text-[12.5px] font-semibold transition ${
                filter === key ? 'bg-white text-navy shadow-sm' : 'text-[#6B7280] hover:text-[#374151]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search supplier, tour, service…"
          className="border border-[#E5E7EB] rounded-[9px] px-3 py-[7px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb w-full sm:w-[260px]"
        />
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center">
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-10 text-center">
          <p className="text-[13px] font-bold text-[#374151] mb-1">No results</p>
          <p className="text-[12px] text-[#9CA3AF]">
            {filter !== 'all' || search ? 'Try adjusting the filter or search.' : 'No supplier assignments yet.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="bg-surface border-b border-[#E5E7EB]">
                  {['Tour / Reference', 'Supplier', 'Service', 'Status', 'Amount', 'Due Date', 'Invoice', 'Payment', ''].map((h, i) => (
                    <th key={i} className="px-[14px] py-[10px] text-left text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F3F6]">
                {filtered.map((a) => (
                  <tr key={a.id} className="hover:bg-surface transition-colors">
                    <td className="px-[14px] py-[12px]">
                      {requestMap[a.requestId] ? (
                        <button
                          onClick={() => navigate(`/staff/requests/${a.requestId}`)}
                          className="text-left hover:text-pb transition"
                        >
                          <p className="text-[13px] font-bold text-[#111827] hover:text-pb">{tourLabel(a)}</p>
                          {tourSubLabel(a) && (
                            <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">{tourSubLabel(a)}</p>
                          )}
                        </button>
                      ) : (
                        <div>
                          <p className="text-[13px] font-bold text-[#111827]">{tourLabel(a)}</p>
                          {tourSubLabel(a) && (
                            <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">{tourSubLabel(a)}</p>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-[14px] py-[12px]">
                      <p className="text-[13px] font-semibold text-[#111827]">{a.supplierName || '—'}</p>
                      <p className="text-[11.5px] text-[#9CA3AF]">{a.supplierEmail}</p>
                      <LinkExpenseCell assignment={a} expenses={expenses} linkedExpenseMap={linkedExpenseMap} requestMap={requestMap} expenseCategories={expenseCategories} />
                    </td>
                    <td className="px-[14px] py-[12px]">
                      <span className="text-[11.5px] font-semibold bg-[#F3F4F6] text-[#374151] px-[8px] py-[3px] rounded-[20px]">
                        {a.serviceType || '—'}
                      </span>
                    </td>
                    <td className="px-[14px] py-[12px]">
                      <span className={`inline-flex items-center text-[11px] font-semibold px-[9px] py-[3px] rounded-[12px] whitespace-nowrap ${STATUS_STYLES[a.status] || 'bg-[#F3F4F6] text-[#6B7280]'}`}>
                        {STATUS_LABELS[a.status] || a.status}
                      </span>
                    </td>
                    <td className="px-[14px] py-[12px]">
                      <AmountCell assignment={a} />
                    </td>
                    <td className="px-[14px] py-[12px]">
                      <DueDateCell assignment={a} />
                    </td>
                    <td className="px-[14px] py-[12px]">
                      <StaffInvoiceCell assignment={a} />
                    </td>
                    <td className="px-[14px] py-[12px]">
                      <PaidStatusCell assignment={a} />
                    </td>
                    <td className="px-[14px] py-[12px]">
                      <button
                        onClick={() => handleDelete(a.id)}
                        disabled={deletingId === a.id}
                        title="Delete assignment"
                        className="text-[#D1D5DB] hover:text-dr transition disabled:opacity-40"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                          <path d="M10 11v6M14 11v6" />
                          <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {Object.keys(totalByCurrency).length > 0 && (
                <tfoot className="bg-surface border-t border-[#E5E7EB]">
                  {Object.entries(totalByCurrency).map(([currency, total], i) => {
                    const sym  = CURRENCY_SYMBOLS[currency] || currency;
                    const fmt  = (n) => n > 0 ? `${sym}${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
                    const paid = paidByCurrency[currency] || 0;
                    const owed = outstandingByCurrency[currency] || 0;
                    return (
                      <tr key={currency}>
                        <td colSpan={4} className="px-[14px] py-[9px] text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px]">
                          {i === 0 ? 'Totals' : ''} {currency}
                        </td>
                        <td className="px-[14px] py-[9px] text-[12.5px] font-extrabold text-[#111827] tabular-nums">
                          {fmt(total)}
                        </td>
                        <td className="px-[14px] py-[9px] text-[11.5px] text-[#9CA3AF] tabular-nums">
                          {paid > 0 && <span className="text-sg font-semibold">{fmt(paid)} paid</span>}
                          {paid > 0 && owed > 0 && ' · '}
                          {owed > 0 && <span className="text-dr font-semibold">{fmt(owed)} owed</span>}
                        </td>
                        <td /><td /><td />
                      </tr>
                    );
                  })}
                </tfoot>
              )}
            </table>
          </div>
          <div className="px-[18px] py-[10px] bg-surface border-t border-[#F1F3F6]">
            <p className="text-[11.5px] text-[#9CA3AF]">
              {filtered.length} assignment{filtered.length !== 1 ? 's' : ''}
              {filter !== 'all' || search ? ' matching current filter' : ''}
              {' · '}Click <strong>+ Add amount</strong> on any row to enter the invoice amount and payment date.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
